import { useState } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import type { Editor } from '@tiptap/react';
import type { TemplateAction } from '@duke-hq/email-templates';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { testIds } from './testIds';

/**
 * Putting the button back. The address field appears only where the send site
 * supplies no link, so a named target cannot be typed wrong.
 */
export function CtaInsert({
  editor,
  action,
}: {
  editor: Editor | null;
  action: TemplateAction;
}): React.ReactElement {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [label, setLabel] = useState('');
  const [address, setAddress] = useState('');
  const writesAddress = action.target === undefined;

  const insert = (): void => {
    editor
      ?.chain()
      .focus()
      .insertContent({
        type: 'cta',
        attrs: { label, target: writesAddress ? address : action.target },
      })
      .run();
    setLabel('');
    setAddress('');
    onClose();
  };

  const incomplete = label.trim() === '' || (writesAddress && address.trim() === '');

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start">
      <PopoverTrigger>
        <Button
          size="sm"
          variant="outline"
          isDisabled={editor === null}
          onClick={onToggle}
          data-testid={testIds.insertCta}
        >
          <Trans>Insert button</Trans>
        </Button>
      </PopoverTrigger>
      <PopoverContent width={pxToRem(320)}>
        <PopoverArrow />
        <PopoverBody>
          <Stack gap={3}>
            <Text fontSize="sm" color="fg.muted">
              {action.opens}
            </Text>
            <FormControl>
              <FormLabel fontSize="sm">
                <Trans>Button text</Trans>
              </FormLabel>
              <Input
                size="sm"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                data-testid={testIds.ctaLabelInput}
              />
            </FormControl>
            {writesAddress ? (
              <FormControl>
                <FormLabel fontSize="sm">
                  <Trans>Address</Trans>
                </FormLabel>
                <Input
                  size="sm"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  data-testid={testIds.ctaTargetInput}
                />
                <FormHelperText fontSize="xs">
                  <Trans>The page this button opens. Ours are filled in for you.</Trans>
                </FormHelperText>
              </FormControl>
            ) : null}
            <Button
              size="sm"
              onClick={insert}
              isDisabled={incomplete}
              data-testid={testIds.ctaConfirm}
            >
              <Trans>Insert</Trans>
            </Button>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
